import '@vanguard/ocean-platform-window-events';

import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { TriageState } from '@interfaces/state';
import { select, Store } from '@ngrx/store';
import { EnvTypes, flowMapper } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/constants/logger.constants';
import { LoggerService } from '@shared/modules/logger/logger';
import { TrackProcessStep } from '@store/actions/adobe-launch/adobe-launch.actions';
import * as fromRootStore from '@store/index';
import { LoadContent, LoadFMFScript, SetInfoForSurvey } from '@store/index';
import { selectTrackProcessStartCalled } from '@store/selectors/adobe-launch/adobe-launch.selectors';
import { selectSpinnerShow } from '@store/selectors/spinner/spinner.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import { distinctUntilChanged, map, Subscription, withLatestFrom } from 'rxjs';
import { VgimAdobeLaunchService } from 'vg-interaction-model';

import { getConfigClientFromRTE } from '../../config/config-client';

declare global {
  interface Window {
    Cypress: any;
    store: any;
    dataLayer: any[] & { _reset?: () => void };
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  subs: Subscription = new Subscription();
  env!: EnvTypes;
  private previousOverflow = '';

  constructor(
    private globalStore: Store<fromRootStore.State>,
    private router: Router,
    private adobeLaunch: VgimAdobeLaunchService,
    private logger: LoggerService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.logIfPageRefresh();

    if (window.Cypress) {
      // Make sure this matches the injected store name. If there is no injected store name in the constructor, you will have to add it
      window.store = this.globalStore;
    }

    this.globalStore.dispatch(LoadContent());
    this.globalStore.dispatch(SetInfoForSurvey());

    // TODO - figure out if something needs to happen here, or this just depended on internal/external
    this.globalStore.dispatch(LoadFMFScript());

    this.env = getConfigClientFromRTE().isProduction ? EnvTypes.PRODUCTION : EnvTypes.DEVELOPMENT;

    this.subs.add(
      this.adobeLaunch.pageInitialized$
        .pipe(
          map(() => {
            let route = this.router.routerState.snapshot.root;
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          withLatestFrom(
            this.globalStore.pipe(select(selectTriageState)),
            this.globalStore.pipe(select(selectTrackProcessStartCalled)),
          ),
        )
        .subscribe(([route, triageState, trackProcessStartCalled]) => {
          this.handleProcessStepTracking(route, triageState, trackProcessStartCalled);
        }),
    );
    this.adobeLaunch.handleAppInit({ pageTemplate: 'CWP' }, this.env, this.router, {
      countryCode: 'us',
      language: 'en',
      siteDescriptor: 'retail',
      platform: 'web',
    });

    this.subs.add(
      this.adobeLaunch.errors.subscribe((err: Error) => {
        this.logger.error({ message: err.message, eventId: 'adobe-launch-failure' }, 0);
      }),
    );

    this.subs.add(
      this.globalStore
        .pipe(select(selectSpinnerShow), distinctUntilChanged())
        .subscribe((showSpinner) => {
          if (showSpinner) {
            this.previousOverflow = this.document.body.style.overflow;
            window.scrollTo(0, 0);
            this.document.body.style.overflow = 'hidden';
          } else {
            this.document.body.style.overflow = this.previousOverflow;
          }
        }),
    );
  }

  private handleProcessStepTracking(
    route: ActivatedRouteSnapshot,
    triageState: TriageState,
    trackProcessStartCalled: boolean,
  ): void {
    const isSecure = this.router.routerState.snapshot.url.startsWith('/secure/');
    if (isSecure && trackProcessStartCalled) {
      const pageId = route.data.pageId;
      const routeFlow = route.data.flow;
      const showAcctType = route.data.accountType;
      const accountType = triageState.accountTypeInheriting;

      const flowType = triageState.flowType ? flowMapper[triageState.flowType] : routeFlow;
      const data = {
        processStep: `coo-${flowType}-${pageId}`,
        processType: `coo-${flowType}-flow`,
        ...(showAcctType && { accountType }),
      };
      this.globalStore.dispatch(TrackProcessStep(data));
    }
  }

  private logIfPageRefresh(): void {
    const navEntry = performance?.getEntriesByType?.(
      'navigation',
    )?.[0] as PerformanceNavigationTiming;
    if (navEntry?.type === 'reload') {
      const url = window.location.pathname;
      this.logger.warn(
        {
          eventId: 'page-refresh-detected',
          message: `Page refresh detected on ${url}`,
        },
        LoggerCodes.NULL_FAILURE,
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
